import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type PFQuality = 'good' | 'high_but_valid' | 'suspicious_low' | 'suspicious_high' | 'missing_data';

export interface DefiProtocol {
  llama_slug: string;
  base_slug?: string;
  name: string;
  tvl_usd: number | null;
  fees_24h_usd: number | null;
  fees_7d_usd: number | null;
  fees_30d_usd: number | null;
  fees_all_time_usd: number | null;
  fees_change_1d: number | null;
  mcap_usd: number | null;
  category: string | null;
  ts: string;
  // Delta fields
  tvl_change_24h: number | null;
  tvl_change_7d: number | null;
  tvl_change_30d: number | null;
  fees_change_7d: number | null;
  fees_change_30d: number | null;
  fees_change_30d_source?: 'exact' | 'baseSlug' | 'historical' | 'missing';
  price_to_fees_ann: number | null;
  price_to_fees_ann_7d: number | null;
  // Data quality indicators
  data_quality: PFQuality;
  pf_quality_30d: PFQuality;
  pf_quality_7d: PFQuality;
  // For historical chart
  gecko_id: string | null;
  gecko_id_source?: 'exact' | 'baseSlug' | 'missing';
}

export interface DefiProtocolsResponse {
  protocols: DefiProtocol[];
  total: number;
  categories: string[];
  updated_at: string;
}

export interface UseDefiProtocolsParams {
  sort?: 'tvl' | 'fees_24h' | 'fees_7d' | 'fees_30d';
  category?: string;
  search?: string;
}

export const useDefiProtocols = (params: UseDefiProtocolsParams = {}) => {
  return useQuery<DefiProtocolsResponse>({
    queryKey: ['defi-protocols', params],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<DefiProtocolsResponse>('get-defi-protocols', {
        body: params
      });
      
      if (error) {
        console.error('[useDefiProtocols] Error:', error);
        throw error;
      }
      
      return data!;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
